import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { resolveMediaAssetUrlCandidates } from '../../../utils/mediaAssetUrl';

const formatApiError = (error, fallback) => {
  const payload = error?.response?.data;
  if (payload?.errors && typeof payload.errors === 'object') {
    const messages = Object.values(payload.errors).find((entry) => Array.isArray(entry) && entry.length > 0);
    if (messages) return messages.join(' ');
  }
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  return fallback;
};

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};

const MediaLibrary = () => {
  const fileInputRef = useRef(null);

  const [assets, setAssets] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const loadAssets = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get('/admin/media-library', {
          params: {
            page,
            search: searchTerm || undefined,
            per_page: 24,
          },
        });

        setAssets(Array.isArray(data?.data) ? data.data : []);
        setMeta({
          current_page: Number(data?.current_page || 1),
          last_page: Number(data?.last_page || 1),
          total: Number(data?.total || 0),
        });
      } catch (requestError) {
        setError(formatApiError(requestError, 'Failed to load media library.'));
      } finally {
        setLoading(false);
      }
    },
    [searchTerm]
  );

  useEffect(() => {
    loadAssets(1);
  }, [loadAssets]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.trim());
  };

  const handleUpload = async () => {
    const files = Array.from(fileInputRef.current?.files || []);
    if (!files.length) {
      setError('Choose one or more images first.');
      setNotice('');
      return;
    }

    setUploading(true);
    setError('');
    setNotice('');

    let uploadedCount = 0;
    let lastError = '';

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post('/admin/media-library', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedCount += 1;
      } catch (requestError) {
        lastError = formatApiError(requestError, `Failed to upload ${file.name}.`);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (uploadedCount > 0) {
      setNotice(`${uploadedCount} image${uploadedCount === 1 ? '' : 's'} uploaded.`);
      await loadAssets(1);
    }

    if (lastError) {
      setError(lastError);
    }

    setUploading(false);
  };

  const handleCopyUrl = async (url) => {
    if (!url) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const helper = document.createElement('textarea');
        helper.value = url;
        helper.style.position = 'fixed';
        helper.style.left = '-9999px';
        document.body.appendChild(helper);
        helper.focus();
        helper.select();
        document.execCommand('copy');
        document.body.removeChild(helper);
      }
      setNotice('Image URL copied.');
      setError('');
    } catch (_) {
      setError('Unable to copy URL on this browser.');
    }
  };

  const handleDelete = async (asset) => {
    if (!asset?.id) return;
    if (!window.confirm(`Delete "${asset.original_name}"?`)) return;

    setDeletingId(asset.id);
    setNotice('');
    setError('');

    try {
      await axios.delete(`/admin/media-library/${asset.id}`);
      setNotice('Image deleted.');

      const currentPage = meta.current_page || 1;
      const nextPage = assets.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await loadAssets(nextPage);
    } catch (requestError) {
      setError(formatApiError(requestError, 'Failed to delete image.'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section className="media-library">
      <div className="card">
        <div className="media-library__header">
          <div>
            <h2>Media Library</h2>
            <p className="text-muted">
              Upload images once and copy their URLs to use across KingdomVisionTV pages.
            </p>
            <p className="text-muted" style={{ marginTop: '0.4rem' }}>
              Total images: {meta.total}
            </p>
          </div>
        </div>

        <div className="media-library__toolbar">
          <form className="media-library__search" onSubmit={handleSearchSubmit}>
            <input
              className="input"
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by image name"
            />
            <button className="btn btn-muted" type="submit">
              Search
            </button>
            <button
              className="btn btn-muted"
              type="button"
              onClick={() => {
                setSearchInput('');
                setSearchTerm('');
              }}
            >
              Clear
            </button>
          </form>

          <div className="media-library__upload">
            <input ref={fileInputRef} type="file" accept="image/*" multiple />
            <button className="btn btn-primary" type="button" onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Images'}
            </button>
          </div>
        </div>

        {notice && <div className="notice notice-success">{notice}</div>}
        {error && <div className="notice notice-danger">{error}</div>}
      </div>

      {loading ? (
        <div className="card">Loading media library...</div>
      ) : assets.length ? (
        <div className="media-library__grid">
          {assets.map((asset) => {
            const urlCandidates = resolveMediaAssetUrlCandidates(asset);
            const resolvedUrl = urlCandidates[0] || asset.url;

            return (
              <article key={asset.id} className="card media-library__item">
                <div className="media-library__preview-wrap">
                  <img
                    src={resolvedUrl}
                    alt={asset.original_name || 'Image asset'}
                    className="media-library__preview"
                    loading="lazy"
                    onError={(event) => {
                      const nextIndex = Number(event.currentTarget.dataset.urlIndex || '0') + 1;
                      if (nextIndex >= urlCandidates.length) {
                        return;
                      }
                      event.currentTarget.dataset.urlIndex = String(nextIndex);
                      event.currentTarget.src = urlCandidates[nextIndex];
                    }}
                  />
                </div>

                <div className="media-library__meta">
                  <strong title={asset.original_name}>{asset.original_name}</strong>
                  <span>{asset.mime_type}</span>
                  <span>
                    {asset.width && asset.height ? `${asset.width}x${asset.height}` : 'Unknown dimensions'} · {formatBytes(asset.size_bytes)}
                  </span>
                  {asset.created_at && (
                    <span>Uploaded: {new Date(asset.created_at).toLocaleString()}</span>
                  )}
                </div>

                <div className="media-library__actions">
                  <button
                    type="button"
                    className="btn btn-muted"
                    disabled={!resolvedUrl}
                    onClick={() => handleCopyUrl(resolvedUrl)}
                  >
                    Copy URL
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={deletingId === asset.id}
                    onClick={() => handleDelete(asset)}
                  >
                    {deletingId === asset.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="card empty-state">
          <h3>No images yet</h3>
          <p className="text-muted">Upload your first image to start building the media library.</p>
        </div>
      )}

      <div className="card media-library__pagination">
        <button
          className="btn btn-muted"
          type="button"
          disabled={meta.current_page <= 1 || loading}
          onClick={() => loadAssets((meta.current_page || 1) - 1)}
        >
          Previous
        </button>
        <span className="text-muted">
          Page {meta.current_page} of {meta.last_page}
        </span>
        <button
          className="btn btn-muted"
          type="button"
          disabled={meta.current_page >= meta.last_page || loading}
          onClick={() => loadAssets((meta.current_page || 1) + 1)}
        >
          Next
        </button>
      </div>
    </section>
  );
};

export default MediaLibrary;
